import type { FC, ReactNode } from 'react'

import Lenis from 'lenis'
import { createContext, useContext, useEffect, useState } from 'react'
import { useRouter } from 'next/router'

const LenisContext = createContext<Lenis | null>(null)

export const useLenis = (): Lenis | null => useContext(LenisContext)

interface SmoothScrollProps {
    children: ReactNode
}

export const SmoothScroll: FC<SmoothScrollProps> = ({ children }): ReactNode => {
    const router = useRouter()
    const [lenis, setLenis] = useState<Lenis | null>(null)

    useEffect(() => {
        const instance = new Lenis({
            autoRaf: true,
            lerp: 0.1,
            smoothWheel: true,
            anchors: { offset: -96 },
            stopInertiaOnNavigate: true,
            respectReducedMotion: true
        })

        setLenis(instance)

        return () => {
            instance.destroy()
            setLenis(null)
        }
    }, [])

    useEffect(() => {
        if (!lenis) return

        const onComplete = (url: string) => {
            if (!url.includes('#')) lenis.scrollTo(0, { immediate: true, force: true })

            lenis.resize()
            lenis.start()
        }

        router.events.on('routeChangeComplete', onComplete)

        return () => {
            router.events.off('routeChangeComplete', onComplete)
        }
    }, [lenis, router.events])

    return (
        <LenisContext.Provider value={lenis}>
            {children}
        </LenisContext.Provider>
    )
}
